import type { WarmCacheContents, WarmCacheSelection } from '@affine/nbstore';
import { LiveData, OnEvent, Service } from '@toeverything/infra';
import { combineLatest, debounceTime, type Subscription } from 'rxjs';

import type { WorkspaceServerService } from '../../cloud';
import { AuthService } from '../../cloud/services/auth';
import type { DocRecord, DocsService } from '../../doc';
import type { FavoriteService } from '../../favorite';
import type { WorkspaceQuotaService } from '../../quota';
import type { WorkspaceLocalState, WorkspacesService } from '../../workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import type { WorkspaceService } from '../../workspace/services/workspace';

const OPENED_AT_KEY = 'warm-cache-opened-at';
const KEPT_KEY = 'warm-cache-kept';
const BUDGET_KEY = 'warm-cache-budget-bytes';
const MB = 1024 * 1024;

/** Default budget per device: Web 200 MB, desktop 2 GB (dec-search-lf-defaults-1020). */
export const WARM_CACHE_DEFAULT_BUDGET_BYTES = BUILD_CONFIG.isElectron
  ? 2048 * MB
  : 200 * MB;

type Plan = { kind: 'pending' } | { kind: 'window'; windowMs: number | null };

/**
 * Chooses which pages this device holds for local full-text search, in
 * order of preference: kept offline, opened on this device (newest first),
 * favorites, edited by the member, changed within the history period of the
 * workspace plan (newest first). The doc sync loads them in this order until
 * the budget in bytes is used; what the warm cache holds is fully searchable.
 * Local workspaces and shared views hold every stored page.
 */
@OnEvent(WorkspaceInitialized, service => service.onWorkspaceInitialized)
export class WarmCacheService extends Service {
  readonly budgetBytes$ = new LiveData<number>(WARM_CACHE_DEFAULT_BUDGET_BYTES);
  /** Pages the warm cache holds now; `all` for every stored page. */
  readonly held$ = new LiveData<'all' | ReadonlySet<string>>(new Set());
  /** Bytes the held pages take as the budget counts them; null for `all`. */
  readonly usedBytes$ = new LiveData<number | null>(null);
  /** The budget in effect; less than budgetBytes$ after the device ran out of space. */
  readonly effectiveBudgetBytes$ = new LiveData<number | null>(null);
  /** Pages of the workspace the member can read, out of the trash. */
  readonly readablePages$: LiveData<number>;
  private plan: Plan = { kind: 'pending' };
  private published = '';
  private readonly subscriptions: Subscription[] = [];
  /** The newest contents of the held pages, not yet handled. */
  private nextBlobs: WarmCacheContents | null = null;
  private storingBlobs = false;
  /** Blobs whose bytes were brought onto this device in this session. */
  private readonly storedThisSession = new Set<string>();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspacesService: WorkspacesService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly quotaService: WorkspaceQuotaService,
    private readonly localState: WorkspaceLocalState,
    private readonly docsService: DocsService,
    private readonly favoriteService: FavoriteService
  ) {
    super();
    this.readablePages$ = LiveData.computed(
      get =>
        get(this.docsService.list.docs$).filter(record => !get(record.trash$))
          .length
    );
  }

  onWorkspaceInitialized() {
    if (this.subscriptions.length > 0) return;
    const engine = this.workspaceService.workspace.engine;
    this.subscriptions.push(
      engine.doc.warmCacheHeld$.subscribe(held => {
        this.held$.next(held === 'all' ? 'all' : new Set(held));
      })
    );
    const workspace = this.workspaceService.workspace;
    // A member's workspace plan decides which changed pages qualify. Local
    // workspaces and shared views have no plan and load nothing in the
    // background: every page they store keeps its full text.
    if (
      workspace.flavour === 'local' ||
      workspace.openOptions.isSharedMode ||
      workspace.openOptions.externalDocAccess
    ) {
      engine.doc.setWarmCache({ kind: 'all' });
      return;
    }
    this.budgetBytes$.next(
      this.localState.get<number | null>(BUDGET_KEY) ??
        WARM_CACHE_DEFAULT_BUDGET_BYTES
    );
    // The doc sync stores only the metadata of blobs; the bytes of the held
    // pages' blobs go into the app's blob cache, which the editor reads offline.
    this.subscriptions.push(
      engine.doc.warmCacheContents$.subscribe(contents => {
        this.usedBytes$.next(contents.bytes);
        this.effectiveBudgetBytes$.next(contents.budgetBytes);
        this.nextBlobs = contents;
        this.storeBlobs().catch(error => {
          // The device has no room for more blobs: hold fewer pages.
          if (
            error instanceof DOMException &&
            error.name === 'QuotaExceededError'
          ) {
            engine.doc.shrinkWarmCache();
            return;
          }
          console.error('failed to keep the blobs of warm pages', error);
        });
      })
    );
    let keptSynced = false;
    this.subscriptions.push(
      this.quotaService.quota.quota$.subscribe(quota => {
        if (!quota) return;
        this.plan = { kind: 'window', windowMs: quota.historyPeriod ?? null };
        this.publish();
        // Kept pages pull their newest content once per start.
        if (!keptSynced) {
          keptSynced = true;
          for (const docId of Object.keys(this.kept())) this.syncKept(docId);
        }
      }),
      combineLatest([
        this.docsService.list.docs$,
        this.favoriteService.favoriteList.list$,
      ])
        .pipe(debounceTime(500))
        .subscribe(() => this.publish())
    );
  }

  /** Whether the warm cache holds a page now. */
  includes(docId: string) {
    const held = this.held$.value;
    return held === 'all' || held.has(docId);
  }

  /** A page opened on this device moves to the front of the warm cache. */
  markOpened(docId: string) {
    this.localState.set(OPENED_AT_KEY, {
      ...this.openedAt(),
      [docId]: Date.now(),
    });
    this.publish();
  }

  /**
   * Keeps a page available offline on this device: it goes first into the
   * warm cache and loads on the next online contact.
   */
  keepOffline(docId: string) {
    this.localState.set(KEPT_KEY, { ...this.kept(), [docId]: Date.now() });
    this.publish();
    this.syncKept(docId);
  }

  /** Pages the member can no longer read leave the warm cache and stop loading. */
  forget(docIds: readonly string[]) {
    const openedAt = this.openedAt();
    const kept = this.kept();
    for (const docId of docIds) {
      delete openedAt[docId];
      if (!(docId in kept)) continue;
      delete kept[docId];
      void this.workspaceService.workspace.engine.doc.releaseDoc(
        docId,
        'offline-keep'
      );
    }
    this.localState.set(OPENED_AT_KEY, openedAt);
    this.localState.set(KEPT_KEY, kept);
    this.publish();
  }

  /** Sets the bytes the warm cache may take on this device. */
  setBudgetBytes(bytes: number) {
    this.localState.set(BUDGET_KEY, bytes);
    this.budgetBytes$.next(bytes);
    this.publish();
  }

  /**
   * Brings the bytes of the held pages' blobs onto this device. The warm
   * cache only loads: a page that leaves it keeps what this device holds.
   */
  private async storeBlobs() {
    if (this.storingBlobs) return;
    this.storingBlobs = true;
    try {
      for (let blobs = this.nextBlobs; blobs; blobs = this.nextBlobs) {
        this.nextBlobs = null;
        const meta = this.workspaceService.workspace.meta;
        for (const blobId of blobs.blobIds) {
          if (this.subscriptions.length === 0) return;
          if (this.storedThisSession.has(blobId)) continue;
          await this.workspacesService.getWorkspaceBlob(meta, blobId);
          this.storedThisSession.add(blobId);
        }
      }
    } finally {
      this.storingBlobs = false;
    }
  }

  private syncKept(docId: string) {
    this.workspaceService.workspace.engine.doc.addDoc(docId, 'offline-keep');
  }

  private kept(): Record<string, number> {
    return { ...this.localState.get<Record<string, number> | null>(KEPT_KEY) };
  }

  private openedAt(): Record<string, number> {
    return {
      ...this.localState.get<Record<string, number> | null>(OPENED_AT_KEY),
    };
  }

  private publish() {
    if (this.plan.kind === 'pending') return;
    const selection: WarmCacheSelection = {
      kind: 'pages',
      docIds: this.order(this.plan.windowMs),
      budgetBytes: this.budgetBytes$.value,
    };
    const key = `${selection.budgetBytes}:${selection.docIds.join(',')}`;
    if (key === this.published) return;
    this.published = key;
    this.workspaceService.workspace.engine.doc.setWarmCache(selection);
  }

  /** The readable pages in order of preference, each once. */
  private order(windowMs: number | null): string[] {
    const records = new Map<string, DocRecord>();
    for (const record of this.docsService.list.docs$.value) {
      if (!record.trash$.value) records.set(record.id, record);
    }
    const byTimeDesc = (entries: Record<string, number>) =>
      Object.entries(entries)
        .sort(([, a], [, b]) => b - a)
        .map(([docId]) => docId);
    const updatedAt = (record: DocRecord) => record.updatedAt$.value ?? 0;
    const changed = [...records.values()].sort(
      (a, b) => updatedAt(b) - updatedAt(a)
    );
    const accountId =
      this.workspaceServerService.server?.scope.get(AuthService).session
        .account$.value?.id;
    const since = windowMs === null ? -Infinity : Date.now() - windowMs;
    const ordered = [
      ...byTimeDesc(this.kept()),
      ...byTimeDesc(this.openedAt()),
      ...this.favoriteService.favoriteList.list$.value
        .filter(favorite => favorite.type === 'doc')
        .map(favorite => favorite.id),
      ...changed
        .filter(record => !!accountId && record.updatedBy$.value === accountId)
        .map(record => record.id),
      ...changed
        .filter(record => updatedAt(record) >= since)
        .map(record => record.id),
    ];
    return [...new Set(ordered)].filter(docId => records.has(docId));
  }

  override dispose() {
    for (const subscription of this.subscriptions) subscription.unsubscribe();
    this.subscriptions.length = 0;
    super.dispose();
  }
}
